import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

/**
 * A headline figure at rest; its whole breakdown when you press it.
 *
 * The figure on the face never repeats inside the breakdown — the face is the answer, the
 * breakdown is the working. Nothing here is a static box.
 */
export const SmartTile = ({
  label,
  value,
  unit,
  sub,
  accent = "hsl(var(--primary))",
  icon: Icon,
  children,
  testId,
  open,
  onToggle,
}) => (
  <div
    className="relative bg-card transition-colors duration-300 hover:bg-muted/50"
    data-testid={testId}
  >
    <span
      className="absolute inset-x-0 top-0 h-[3px] origin-left transition-transform duration-500"
      style={{ backgroundColor: accent, transform: open ? "scaleX(1)" : "scaleX(0.18)" }}
    />
    <button
      type="button"
      data-testid={`${testId}-face`}
      aria-expanded={!!open}
      onClick={onToggle}
      className="group w-full px-6 pb-5 pt-7 text-left"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="label-caps">{label}</span>
        {Icon && (
          <Icon
            className="h-4 w-4 shrink-0 transition-colors duration-300"
            style={{ color: open ? accent : "hsl(var(--muted-foreground))" }}
          />
        )}
      </span>
      <span className="mt-4 flex items-baseline gap-1.5">
        <span
          className="font-data text-[2.6rem] font-black leading-none tracking-tighter tabular-nums"
          data-testid={`${testId}-value`}
        >
          {value}
        </span>
        {unit && (
          <span className="font-data text-sm font-bold text-muted-foreground">{unit}</span>
        )}
      </span>
      <span className="mt-2 flex items-end justify-between gap-3">
        <span className="max-w-[22ch] text-[11px] leading-snug text-muted-foreground">{sub}</span>
        <span className="flex shrink-0 items-center gap-1 font-display text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 group-hover:text-foreground">
          {open ? "close" : "break it down"}
          <ChevronDown
            className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </span>
      </span>
    </button>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div
            className="border-t border-border bg-muted/40 px-6 py-5"
            data-testid={`${testId}-breakdown`}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/** A ring of the parts that make up a whole — the donut a tile explodes into. */
export const Donut = ({ slices = [], total, caption, testId }) => {
  const [hot, setHot] = useState(-1);
  const sum = slices.reduce((s, x) => s + (x.value || 0), 0) || 1;
  const R = 54;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="flex flex-wrap items-center gap-6" data-testid={testId}>
      <svg viewBox="0 0 140 140" className="h-[150px] w-[150px] shrink-0 -rotate-90">
        <circle cx="70" cy="70" r={R} fill="none" stroke="hsl(var(--border))" strokeWidth="18" />
        {slices.map((s, i) => {
          const len = ((s.value || 0) / sum) * C;
          const dash = `${len} ${C - len}`;
          const el = (
            <circle
              key={s.key}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={s.colour}
              strokeWidth={hot === i ? 24 : 18}
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              style={{ transition: "stroke-width 200ms ease" }}
              onMouseEnter={() => setHot(i)}
              onMouseLeave={() => setHot(-1)}
            />
          );
          offset += len;
          return el;
        })}
        <text
          x="70"
          y="66"
          className="font-data"
          textAnchor="middle"
          fontSize="22"
          fontWeight="800"
          fill="hsl(var(--foreground))"
          transform="rotate(90 70 70)"
        >
          {hot >= 0 ? slices[hot].value : total}
        </text>
        <text
          x="70"
          y="80"
          textAnchor="middle"
          fontSize="7.5"
          letterSpacing="1.4"
          fill="hsl(var(--muted-foreground))"
          transform="rotate(90 70 70)"
        >
          {(hot >= 0 ? slices[hot].label : caption || "").toUpperCase().slice(0, 22)}
        </text>
      </svg>
      <ul className="min-w-[200px] flex-1 space-y-1.5">
        {slices.map((s, i) => (
          <li key={s.key}>
            <button
              type="button"
              data-testid={`${testId}-${s.key}`}
              onMouseEnter={() => setHot(i)}
              onMouseLeave={() => setHot(-1)}
              onClick={s.onClick}
              disabled={!s.onClick}
              className="flex w-full items-baseline gap-2.5 py-0.5 text-left transition-opacity duration-200 disabled:cursor-default"
              style={{ opacity: hot === -1 || hot === i ? 1 : 0.4 }}
            >
              <span
                className="mt-1 inline-block h-2.5 w-2.5 shrink-0"
                style={{ backgroundColor: s.colour }}
              />
              <span className="flex-1 truncate text-xs">{s.label}</span>
              <span className="font-data text-xs font-bold tabular-nums">{s.value}</span>
              <span className="font-data w-11 shrink-0 text-right text-[10px] text-muted-foreground">
                {Math.round(((s.value || 0) / sum) * 100)}%
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Two or three rupee figures side by side, as bars that can be compared at a glance. */
export const CompareBars = ({ bars = [], testId }) => {
  const top = Math.max(1, ...bars.map((b) => Number(b.value) || 0));
  return (
    <div className="space-y-4" data-testid={testId}>
      {bars.map((b, i) => (
        <div key={b.key} data-testid={`${testId}-${b.key}`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-medium">{b.label}</span>
            <span className="font-data text-sm font-bold tabular-nums" style={{ color: b.colour }}>
              {b.readout}
            </span>
          </div>
          <div className="mt-1.5 h-2.5 w-full bg-border">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((Number(b.value) || 0) / top) * 100}%` }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="h-2.5"
              style={{ backgroundColor: b.colour }}
            />
          </div>
          {b.note && <p className="mt-1 text-[10px] text-muted-foreground">{b.note}</p>}
        </div>
      ))}
    </div>
  );
};
